/**
 * Haptic Feedback System — Duolingo-inspired
 *
 * Philosophy (from Duolingo & Material Design haptic guidelines):
 *  • Every meaningful interaction gets subtle tactile feedback
 *  • Different intensities communicate different meanings
 *  • Haptics COMPLEMENT visuals — they never stand alone
 *  • Celebrations get distinct multi-pulse patterns
 *  • Errors/warnings feel "heavier" than success
 *  • Navigation & toggles get the lightest touch
 *  • Never annoy — keep durations short, skip on desktop
 */

export type HapticType =
  | 'light' // default tap, navigation, minor interaction
  | 'medium' // CTA press, form submit, toggle change
  | 'success' // operation succeeded, login, save, send
  | 'warning' // caution states, approaching limits
  | 'error' // something went wrong
  | 'celebrate' // big wins — project created, onboarding complete, payment success
  | 'toggle' // switch / checkbox flip — quick double-tap feel
  | 'nav' // route change / tab switch — barely-there tick
  | 'modal' // modal / sheet opening — soft thud
  | 'delete'; // destructive action confirmation

const patterns: Record<HapticType, number | number[]> = {
  light: 6,
  medium: 12,
  success: [8, 50, 12],
  warning: 16,
  error: [14, 40, 14],
  celebrate: [10, 30, 10, 30, 14],
  toggle: [6, 30, 6],
  nav: 4,
  modal: 10,
  delete: [12, 40, 18]
};

export function canHaptic(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'vibrate' in navigator &&
    typeof navigator.vibrate === 'function'
  );
}

/** Only fire on touch devices */
function isTouchDevice(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

export function haptic(type: HapticType = 'light') {
  if (!canHaptic()) return;
  if (!isTouchDevice()) return;
  navigator.vibrate(patterns[type]);
}

// ── Contextual helpers ─────────────────────────────────────────
// These give semantic names so call-sites read clearly.

/** Fire when a toast / notification appears */
export function hapticNotify(kind: 'success' | 'error' | 'warning' | 'info') {
  const map: Record<string, HapticType> = {
    success: 'success',
    error: 'error',
    warning: 'warning',
    info: 'light'
  };
  haptic(map[kind] ?? 'light');
}

/** Fire when a modal / bottom-sheet opens */
export function hapticModal() {
  haptic('modal');
}

/** Fire on router navigation (mobile tab bar, sidebar item) */
export function hapticNav() {
  haptic('nav');
}

/** Fire on toggle / switch / checkbox change */
export function hapticToggle() {
  haptic('toggle');
}

/** Fire on big celebration moments */
export function hapticCelebrate() {
  haptic('celebrate');
}

/** Fire on destructive action (delete, remove) */
export function hapticDelete() {
  haptic('delete');
}

// ── Global tap listener ────────────────────────────────────────

const HAPTIC_TYPES = new Set<string>([
  'light',
  'medium',
  'success',
  'warning',
  'error',
  'celebrate',
  'toggle',
  'nav',
  'modal',
  'delete'
]);

function getHapticType(el: HTMLElement): HapticType {
  const data = el.getAttribute('data-haptic');
  if (data === 'none') return 'light';
  if (data && HAPTIC_TYPES.has(data)) return data as HapticType;
  return 'light';
}

export function initHapticInteractions(): (() => void) | undefined {
  if (typeof window === 'undefined') return undefined;

  const handler = (event: PointerEvent) => {
    if (!canHaptic() || !isTouchDevice()) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const el = target.closest(
      [
        '[data-haptic]',
        'button',
        'a',
        '[role="button"]',
        '[role="tab"]',
        '[role="switch"]',
        '[role="menuitem"]',
        'input[type="checkbox"]',
        'input[type="radio"]',
        'select',
        '.sidebar-item',
        '.nav-item',
        '.tab-item',
        '.persona-pill',
        '.next-button',
        '.submit-button',
        '.cta-button',
        '.Polaris-Button',
        '.Polaris-ActionList__Item',
        '.Polaris-ButtonBase',
        '.Polaris-Tabs__Tab'
      ].join(', ')
    ) as HTMLElement | null;

    if (!el) return;
    if (el.getAttribute('data-haptic') === 'none') return;

    haptic(getHapticType(el));
  };

  window.addEventListener('pointerdown', handler, { passive: true });

  return () => {
    window.removeEventListener('pointerdown', handler);
  };
}
